﻿/* eslint-disable no-undef */
import './app/config/apiInterceptor';
/* eslint-disable no-unused-vars */
import './app/services/ApiService'; // Capital 'A'
import React, { useState, useEffect } from 'react';
import { LogBox, View, Text, ActivityIndicator, TextInput , Alert,
} from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createStackNavigator } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { StripeProvider } from '@stripe/stripe-react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { syncAfterLogin } from './app/services/companyProfileSync';
import * as Crypto from 'expo-crypto';
import 'react-native-gesture-handler';

// React Native Screens
import { enableScreens } from 'react-native-screens';
enableScreens();

// Theme
import { LightTheme, DarkTheme as CustomDarkTheme } from './theme/AppTheme';

// Context Providers
import { AuthProvider } from './app/context/AuthContext';
import { LearningProvider } from './app/context/LearningContext';
import { ProfessionalProvider } from './app/context/ProfessionalContext';
import { BusinessProvider } from './app/context/BusinessContext';
import { GeneralProvider } from './app/context/GeneralContext';
import { ProfessionalOnboardingProvider } from './app/context/ProfessionalOnboardingContext';
import { DatabaseProvider } from './app/context/DatabaseContext';
import businessAuthCoordinator from './app/context/BusinessAuthenticationCoordinator';
import { ServiceProvider } from './app/context/ServiceContext';
// Services
import InterviewIntegrationService from './app/services/InterviewIntegrationService';
import InterviewWorkflowAutomation from './app/services/InterviewWorkflowAutomation';
import { BusinessStateProvider } from './app/services/BusinessStateManager';
import businessNavigationService from './app/services/BusinessNavigationService';
import { BusinessErrorBoundary } from './app/services/BusinessErrorHandler';
import businessProfessionalIntegration from './app/services/BusinessProfessionalIntegration';
import { DataManagementService } from './app/services/DataManagementService';
import ServerDiagnostic from './app/services/ServerDiagnostic';
import FloatingDebugButton from './app/services/FloatingDebugButton';
import { initializeDarkMode } from './app/utils/darkModeSafety';
import './patchDarkMode'; // Add this FIRST
import { ProjectProvider } from './app/context/ProjectContext';
// ===== SAFE GLOBAL INITIALIZATION - FIRST THING =====
console.log('🔧 Applying global hotfix for apiService initialization...');

// Create a minimal safe global object if it doesn't exist
try {
  if (typeof global === 'undefined') {
    console.log('⚠️ Global is undefined, creating window.global fallback');
    if (typeof window !== 'undefined') {
      window.global = window;
    }
  }
} catch (e) {
  console.log('⚠️ Could not access global/window:', e.message);
}

// SAFE initialization function
const safeInitializeGlobalServices = () => {
  try {
    console.log('🔧 Creating global.apiService...');
    
    // Create global if it doesn't exist
    if (typeof global === 'undefined') {
      console.log('❌ Global is undefined, cannot create apiService');
      return false;
    }
    
    // Initialize apiService with safe defaults
    global.apiService = {
      initialize: async () => {
        console.log('✅ Global API service initialized');
        return { success: true };
      },
      isReady: true,
      version: '1.0.0',
      features: {
        auth: true,
        crm: true,
        inventory: true,
        finance: true,
        sales: true,
        hr: true,
        production: true,
      },
      // Add default property safely
      default: {
        initialize: async () => {
          console.log('✅ Default API service initialized');
          return { success: true };
        }
      }
    };
    
    // Initialize other global objects
    global.networkStatus = {
      isConnected: true,
      isInternetReachable: true,
      type: 'wifi',
      lastUpdated: new Date().toISOString()
    };
    
    global.lastNavigationState = null;
    global.initializeDatabaseServices = async () => {
      console.log('✅ Database initialized (quick fix)');
      return {
        success: true,
        database: 'connected',
        status: 'OK',
        timestamp: new Date().toISOString()
      };
    };
    
    console.log('✅ Global services initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Error initializing global services:', error);
    return false;
  }
};

// Run initialization immediately
safeInitializeGlobalServices();

// Screens - Splash & Authentication
import SplashScreen from './app/screens/professional/SplashScreen';
import LoadingScreen from './screens/LoadingScreen';
import UserTypeSelectionScreen from './app/screens/professional/UserTypeSelectionScreen';
import RegisterScreen from './app/screens/professional/RegisterScreen';
import ProfessionalLoginScreen from './app/screens/professional/ProfessionalLoginScreen';
import ProfessionalSignupScreen from './app/screens/professional/ProfessionalSignupScreen';
import BusinessLoginScreen from './app/screens/business/core/BusinessLoginScreen';
import BusinessSignUpScreen from './app/screens/business/core/BusinessSignUpScreen';
import LearningLoginScreen from './app/screens/learning/LearningLoginScreen';
import LearningSignupScreen from './app/screens/learning/LearningSignupScreen';
import ForgotPasswordScreen from './app/screens/business/core/ForgotPasswordScreen';

// Welcome Screens
import WelcomeProfessionalScreen from './app/screens/professional/WelcomeProfessionalScreen';
import WelcomeBusinessScreen from './app/screens/business/core/WelcomeBusinessScreen';
import WelcomeLearningScreen from './app/screens/learning/WelcomeLearningScreen';

// Home & Main Screens
import HomeScreen from './screens/HomeScreen';
import ProfessionalHomeScreen from './app/screens/professional/ProfessionalHomeScreen';
import ProfileScreen from './app/screens/professional/ProfileScreen';
import EditProfileScreen from './app/screens/professional/EditProfileScreen';
import ActionScreen from './app/screens/professional/ActionScreen';

// Dashboard Screens
import ProfessionalDashboardScreen from './app/screens/professional/ProfessionalDashboardScreen';
import BusinessDashboardScreen from './app/screens/business/core/BusinessDashboardScreen';
import AddEmployeeScreen from './app/screens/business/hr/AddEmployeeScreen';
import LearningDashboard from './app/screens/learning/LearningDashboard';
import MultiUserBusinessDashboard from './app/screens/business/core/MultiUserBusinessDashboard';

// Job Screens
import FindJobsScreen from './app/screens/professional/FindJobsScreen';
import BrowseJobsScreen from './app/screens/professional/BrowseJobsScreen';
import JobDetailsScreen from './app/screens/professional/JobDetailsScreen';
import SubmitForJobsScreen from './app/screens/professional/SubmitForJobsScreen';
import FreelanceJobsScreen from './app/screens/professional/FreelanceJobsScreen';
import FullTimeJobsScreen from './app/screens/professional/FullTimeJobsScreen';
import PartTimeJobsScreen from './app/screens/professional/PartTimeJobsScreen';
import SearchJobsScreen from './app/screens/professional/SearchJobsScreen';
import RemoteJobsScreen from './app/screens/professional/RemoteJobsScreen';
import EntryLevelJobsScreen from './app/screens/professional/EntryLevelJobsScreen';
import AIJobMatching from './app/screens/professional/AIJobMatching';

// Company & Business Screens
import CompanyInfoScreen from './app/screens/professional/CompanyInfoScreen';
import BusinessCompanyInfoScreen from './app/screens/business/core/BusinessCompanyInfoScreen';
import CompanyProfileScreen from './app/screens/business/core/CompanyProfileScreen';
import CompanyManagementScreen from './app/screens/business/core/CompanyManagementScreen';
import MarketInsightsScreen from './app/screens/business/analytics/MarketInsightsScreen';


// Internship & Career Screens
import InternshipOpportunitiesScreen from './app/screens/professional/InternshipOpportunitiesScreen';
import InternshipDetailsScreen from './app/screens/professional/InternshipDetailsScreen';
import InternshipApplicationScreen from './app/screens/professional/InternshipApplicationScreen';
import GraduateProgramsScreen from './app/screens/professional/GraduateProgramsScreen';
import GraduateProgramApplicationScreen from './app/screens/professional/GraduateProgramApplicationScreen';
import GraduateProgramDetailsScreen from './app/screens/professional/GraduateProgramDetailsScreen';
import CareerFairsScreen from './app/screens/professional/CareerFairsScreen';
import CareerFairRegistrationScreen from './app/screens/professional/CareerFairRegistrationScreen';
import CareerFairDetailsScreen from './app/screens/professional/CareerFairDetailsScreen';
import MentorshipProgramsScreen from './app/screens/professional/MentorshipProgramsScreen';
import MentorshipApplicationScreen from './app/screens/professional/MentorshipApplicationScreen';
import MentorshipProgramDetailsScreen from './app/screens/professional/MentorshipProgramDetailsScreen';
import CareerCoachingScreen from './app/screens/professional/CareerCoachingScreen';
import CareerEventScreen from './app/screens/professional/CareerEventScreen';

// Application Management
import JobApplicationScreen from './app/screens/professional/JobApplicationScreen';
import JobApplicationsScreen from './app/screens/professional/JobApplicationsScreen';
import ApplicationDetailsScreen from './app/screens/professional/ApplicationDetailsScreen';
import ApplicationsTrackingScreen from './app/screens/professional/ApplicationsTrackingScreen';
import JobsOpportunitiesScreen from './app/screens/professional/JobsOpportunitiesScreen';
import JobsOffersScreen from './app/screens/professional/JobsOffersScreen';
import OfferHistoryScreen from './app/screens/professional/OfferHistoryScreen';
import OfferDetails from './app/screens/professional/OfferDetails';
import AllOpportunitiesScreen from './app/screens/professional/AllOpportunitiesScreen';
import MyDocumentsScreen from './app/screens/professional/MyDocumentsScreen';

// Profile Management
import PortfolioScreen from './app/screens/professional/PortfolioScreen';
import ProfileRatingScreen from './app/screens/professional/ProfileRatingScreen';
import AccountSettingsScreen from './app/screens/professional/AccountSettingsScreen';
import AllActivityScreen from './app/screens/professional/AllActivityScreen';
import ActivityDetailScreen from './app/screens/professional/ActivityDetailScreen';
import AddExperience from './app/screens/professional/AddExperience';
import EditExperience from './app/screens/professional/EditExperience';
import AddEducation from './app/screens/professional/AddEducation';
import EditEducation from './app/screens/professional/EditEducation';
import AddSkill from './app/screens/professional/AddSkill';
import EditSkill from './app/screens/professional/EditSkill';
import AddCertification from './app/screens/professional/AddCertification';
import AllWorkHistoryScreen from './app/screens/professional/AllWorkHistoryScreen';
import AllProjectsScreen from './app/screens/professional/AllProjectsScreen';
import AllSkillsScreen from './app/screens/professional/AllSkillsScreen';
import AllCertificationsScreen from './app/screens/professional/AllCertificationsScreen';
import AllEducationScreen from './app/screens/professional/AllEducationScreen';

// Resume & Cover Letter
import BuildResumeScreen from './app/screens/professional/BuildResumeScreen';
import UploadResumeScreen from './app/screens/professional/UploadResumeScreen';
import ResumePreviewScreen from './app/screens/professional/ResumePreviewScreen';
import EditEvaluateResumeScreen from './app/screens/professional/EditEvaluateResumeScreen';
import CreateCoverLetterScreen from './app/screens/professional/CreateCoverLetterScreen';

// Learning Screens
import LearningScreen from './app/screens/learning/LearningScreen';
import DevelopmentTrainingScreen from './app/screens/professional/DevelopmentTrainingScreen';
import CourseDetailsScreen from './app/screens/learning/CourseDetailsScreen';
import PCourseDetails from './app/screens/professional/PCourseDetails';
import CoursePlayerScreen from './app/screens/professional/CoursePlayerScreen';
import CategoryCoursesScreen from './app/screens/professional/CategoryCoursesScreen';
import CourseSearchScreen from './app/screens/professional/CourseSearchScreen';
import WebinarRegistrationScreen from './app/screens/professional/WebinarRegistrationScreen';
import MyLearningHistoryScreen from './app/screens/professional/MyLearningHistoryScreen';
import PreviousCoursesScreen from './app/screens/learning/PreviousCoursesScreen';
import SkillsUpgradeCoursesScreen from './app/screens/professional/SkillsUpgradeCoursesScreen';
import LearningCoursesScreen from './app/screens/learning/LearningCoursesScreen';
import LearningProfileScreen from './app/screens/learning/LearningProfileScreen';
import EngineeringBooksScreen from './app/screens/learning/EngineeringBooksScreen';
import VirtualInternshipScreen from './app/screens/learning/VirtualInternshipScreen';
import BookDetailScreen from './app/screens/learning/BookDetailScreen';
import FreeCoursesScreen from './app/screens/learning/FreeCoursesScreen';
import PDFViewer from './app/screens/learning/PDFViewer';
import MyCourses from './app/screens/learning/MyCourses';
import BookPreview from './app/screens/learning/BookPreview';
import FullTOC from './app/screens/learning/FullTOC';
import CourseContent from './app/screens/learning/CourseContent';
import CourseAssignments from './app/screens/learning/CourseAssignments';
import TodaySchedule from './app/screens/learning/TodaySchedule';
import Bookmarks from './app/screens/learning/Bookmarks';
import Downloads from './app/screens/learning/Downloads';
import Certificates from './app/screens/learning/Certificates';
import CourseCatalog from './app/screens/learning/CourseCatalog';
import LiveSessions from './app/screens/learning/LiveSessions';
import ScheduleSession from './app/screens/learning/ScheduleSession';
import UKUniversities from './app/screens/learning/UKUniversities';
import UniversityDetails from './app/screens/learning/UniversityDetails';
import UniversityApplications from './app/screens/learning/UniversityApplications';
import Scholarships from './app/screens/learning/Scholarships';
import PracticeExams from './app/screens/learning/PracticeExams';
import VideoLibrary from './app/screens/learning/VideoLibrary';
import ResearchPapers from './app/screens/learning/ResearchPapers';
import Internships from './app/screens/learning/Internships';
import InternshipsApply from './app/screens/learning/InternshipsApply';
import SavedInternships from './app/screens/learning/SavedInternships';
import JobBoard from './app/screens/learning/JobBoard';
import JobApply from './app/screens/learning/JobApply';
import CareerGuidance from './app/screens/learning/CareerGuidance';
import StudyGroups from './app/screens/learning/StudyGroups';
import Forums from './app/screens/learning/Forums';
import AskExpert from './app/screens/learning/AskExpert';
import LearningNotifications from './app/screens/learning/LearningNotifications';
import LearningHelpSupport from './app/screens/learning/LearningHelpSupport';
import ProfileSettings from './app/screens/learning/ProfileSettings';
import ScholarshipFilter from './app/screens/learning/ScholarshipFilter';
import ScholarshipTips from './app/screens/learning/ScholarshipTips';
import FontSizeSettings from './app/screens/learning/FontSizeSettings';
import LanguageSettings from './app/screens/learning/LanguageSettings';
import SearchScreen from './app/screens/learning/SearchScreen';
import LiveVideoCoursesScreen from './app/screens/learning/LiveVideoCoursesScreen';
import LiveVideoSessionScreen from './app/screens/learning/LiveVideoSessionScreen';
import LiveCourseDetailsScreen from './app/screens/learning/LiveCourseDetailsScreen';
import SessionRecordingScreen from './app/screens/learning/SessionRecordingScreen';
import MoreScreen from './app/screens/learning/MoreScreen';
import EditLearningProfileScreen from './app/screens/learning/EditLearningProfileScreen';
import LearningCertificateScreen from './app/screens/learning/LearningCertificateScreen';
import CourseLearningScreen from './app/screens/learning/CourseLearningScreen';
import AssignmentScreen from './app/screens/learning/AssignmentScreen';
import QuizScreen from './app/screens/learning/QuizScreen';
import AchievementsScreen from './app/screens/learning/AchievementsScreen';
import CourseLearningPMScreen from './app/screens/learning/CourseLearningPMScreen';
import AILearningAssistantScreen from './app/screens/learning/AILearningAssistantScreen';

// Tests & Interviews
import TestsInterviewsScreen from './app/screens/professional/TestsInterviewsScreen';
import InterviewDetailsScreen from './app/screens/professional/InterviewDetailsScreen';
import VideoInterviewScreen from './app/screens/professional/VideoInterviewScreen';
import InterviewPrep from './app/screens/professional/InterviewPrep';
import RescheduleInterview from './app/screens/professional/RescheduleInterview';
import AssessmentDetails from './app/screens/professional/AssessmentDetails';
import AssessmentResultsScreen from './app/screens/professional/AssessmentResultsScreen';
import AIVideoInterviewScreen from './app/screens/professional/AIVideoInterviewScreen';
import InterviewHistoryScreen from './app/screens/professional/InterviewHistoryScreen';

// Communication
import MessagesScreen from './app/screens/professional/MessagesScreen';
import NewMessage from './app/screens/professional/NewMessage';
import NotificationsScreen from './app/screens/professional/NotificationsScreen';
import ChatDetailScreen from './app/screens/professional/ChatDetailScreen';

// Services & Support
import ServicesScreen from './app/screens/professional/ServicesScreen';
import SupportScreen from './app/screens/professional/SupportScreen';
import Settings from './app/screens/professional/Settings';
import PrivacySettingsScreen from './app/screens/professional/PrivacySettingsScreen';

// Events & News
import Events from './screens/Events';
import EventDetail from './screens/EventDetail';
import UKInsights from './screens/UKInsights';
import NewsTicker from './screens/NewsTicker';
import NewsDetailScreen from './screens/NewsDetailScreen';
import NewsListScreen from './screens/NewsListScreen';
import AddNewsScreen from './screens/AddNewsScreen';

// Payment & Subscription
import CoursePurchaseScreen from './app/screens/professional/CoursePurchaseScreen';
import PaymentHistoryScreen from './app/screens/professional/PaymentHistoryScreen';
import SubscriptionStatusScreen from './app/screens/professional/SubscriptionStatusScreen';
import CancelSubscriptionScreen from './app/screens/professional/CancelSubscriptionScreen';
import StripePaymentIntegration from './app/screens/professional/ProfessionalStripePayment';
import PaymentsScreen from './app/screens/professional/PaymentsScreen';
// ── Added by navigation patch: routes called from screens but not previously imported ──
import PaymentScreen from './app/screens/professional/PaymentScreen';
import PaymentScenariosScreen from './app/screens/professional/PaymentScenariosScreen';
import BusinessSubscriptionSystem from './app/screens/business/finance/BusinessSubscriptionSystem';
import BusinessPaymentSystem from './app/screens/business/finance/BusinessPaymentSystem';
import ManageSubscriptionScreen from './app/screens/business/finance/ManageSubscriptionScreen';

// Skill Development
import SkillDevelopmentScreen from './app/screens/professional/SkillDevelopmentScreen';
import CourseEnrollmentScreen from './app/screens/professional/CourseEnrollmentScreen';
import SCourseDetailsScreen from './app/screens/professional/SCourseDetailsScreen';
import SkillAssessmentScreen from './app/screens/professional/SkillAssessmentScreen';
import SkillProgressScreen from './app/screens/professional/SkillProgressScreen';

// Analytics
import AnalyticsScreen from './app/screens/professional/AnalyticsScreen';
import CompanyAnalytics from './app/screens/business/analytics/CompanyAnalytics';
import BusinessIntelligenceScreen from './app/screens/business/analytics/BusinessIntelligenceScreen';

// Job Alerts
import JobAlertsScreen from './app/screens/professional/JobAlertsScreen';

// Networking
import NetworkingScreen from './app/screens/professional/NetworkingScreen';

// Multi-user Business
import MultiUserBusinessSignup from './app/screens/business/core/MultiUserBusinessSignup';
import TeamManagementScreen from './app/screens/business/other/TeamManagementScreen';

// Business Management
import ManageProjectsScreen from './app/screens/business/projects/ManageProjectsScreen';
import PostJobScreen from './app/screens/business/recruitment/PostJobScreen';
import FinancialManagementScreen from './app/screens/business/finance/FinancialManagementScreen';
import CampaignEmailTemplatesScreen from './app/screens/business/marketing/CampaignEmailTemplatesScreen';
import ComplianceRegulatoryScreen from './app/screens/business/compliance/ComplianceRegulatoryScreen';
import SuppliersVendorList from './app/screens/business/vendors/SuppliersVendorList';
import SubcontractorsVendorList from './app/screens/business/vendors/SubcontractorsVendorList';
import TendersManagement from './app/screens/business/vendors/TendersManagement';
import InvoicesManagement from './app/screens/business/finance/InvoicesManagement';
import BusinessTestsInterviewsScreen from './app/screens/business/other/BusinessTestsInterviewsScreen';
import BusinessVideoConferenceScreen from './app/screens/business/other/BusinessVideoConferenceScreen';
import ComplianceRegulatory from './app/screens/business/compliance/ComplianceRegulatory';
import MarketingPromotion from './app/screens/business/marketing/MarketingPromotion';
import BusinessVideoInterviewScreen from './app/screens/business/core/BusinessVideoInterviewScreen';
import InterviewManagementScreen from './app/screens/business/recruitment/InterviewManagementScreen';
import Applicants from './app/screens/business/recruitment/Applicants';
import TestApplicantsScreen from './app/screens/business/recruitment/TestApplicantsScreen';
import CandidateManagementScreen from './app/screens/business/recruitment/CandidateManagementScreen';
import AddNewTestScreen from './app/screens/business/recruitment/AddNewTestScreen';
import TestResultsScreen from './app/screens/business/recruitment/TestResultsScreen';
import SmartContractPaymentScreen from './app/screens/business/finance/SmartContractPaymentScreen';
import QualifiedApplicantsScreen from './app/screens/business/recruitment/QualifiedApplicantsScreen';
import RecruitmentFlowComponent from './app/screens/business/recruitment/RecruitmentFlowComponent';
import CandidateSelectionScreen from './app/screens/business/recruitment/CandidateSelectionScreen';
import VideoInterviewManagementScreen from './app/screens/business/recruitment/VideoInterviewManagementScreen';
import InPersonInterviewScreen from './app/screens/business/recruitment/InPersonInterviewScreen';
import JobOfferManagementScreen from './app/screens/business/recruitment/JobOfferManagementScreen';
import CreateJobOfferScreen from './app/screens/business/recruitment/CreateJobOfferScreen';
import JobOfferLetterTemplate from './app/screens/business/recruitment/JobOfferLetterTemplate';
import JobOfferEmailTemplate from './app/screens/business/recruitment/JobOfferEmailTemplate';
import AcceptedOffersScreen from './app/screens/business/recruitment/AcceptedOffersScreen';
import InternshipManagementScreen from './app/screens/business/other/InternshipManagementScreen';
import AddProjectScreen from './app/screens/business/projects/AddProjectScreen';
import ApplicantSelectionScreen from './app/screens/business/recruitment/ApplicantSelectionScreen';
import ScheduleInterviewScreen from './app/screens/business/recruitment/ScheduleInterviewScreen';
import ApplicantsPoolScreen from './app/screens/business/recruitment/ApplicantsPoolScreen';
import ActiveJobsScreen from './app/screens/business/recruitment/ActiveJobsScreen';
import MakeOfferScreen from './app/screens/business/recruitment/MakeOfferScreen';
import RatingsReviewsScreen from './app/screens/business/other/RatingsReviewsScreen';

// Business User Profile
import BusinessUserProfileScreen from './app/screens/business/core/BusinessUserProfileScreen';

// Sales & Marketing
import BlogEditorScreen from './app/screens/business/marketing/BlogEditorScreen';
import CampaignDetailsScreen from './app/screens/business/marketing/CampaignDetailsScreen';
import AIContentAssistantScreen from './app/screens/professional/AIContentAssistantScreen';
import CampaignAIContentAssistantScreen from './app/screens/business/marketing/CampaignAIContentAssistantScreen';
import MachineLearningScreen from './app/screens/business/ai/MachineLearningScreen';
import CreateCampaignScreen from './app/screens/business/marketing/CreateCampaignScreen';
import EmailTemplatesScreen from './app/screens/business/marketing/EmailTemplatesScreen';
import VideoEditorScreen from './app/screens/business/marketing/VideoEditorScreen';
import ImageCreatorScreen from './app/screens/business/marketing/ImageCreatorScreen';
import CampaignListScreen from './app/screens/business/marketing/CampaignListScreen';
import CampaignAnalyticsDashboard from './app/screens/business/marketing/CampaignAnalyticsDashboard';
import AddCampaignAssetScreen from './app/screens/business/marketing/AddCampaignAssetScreen';
import ScheduleSettingsScreen from './app/screens/business/marketing/ScheduleSettingsScreen';
import TargetingSettingsScreen from './app/screens/business/marketing/TargetingSettingsScreen';
import BudgetSettingsScreen from './app/screens/business/marketing/BudgetSettingsScreen';
import EditCampaignScreen from './app/screens/business/marketing/EditCampaignScreen';
import ExportCampaignScreen from './app/screens/business/marketing/ExportCampaignScreen';
import NotificationsCampaign from './app/screens/business/marketing/NotificationsCampaign';
import NotificationSettingsCampaign from './app/screens/business/marketing/NotificationSettingsCampaign';
 import NotificationScheduleCampaign from './app/screens/business/marketing/NotificationScheduleCampaign';
import NotificationHistoryCampaign from './app/screens/business/marketing/NotificationHistoryCampaign';

// Project Management
import ProjectAnalyticsScreen from './app/screens/business/projects/ProjectAnalyticsScreen';
import ProjectDetailsScreen from './app/screens/business/projects/ProjectDetailsScreen';
import ProjectMilestonesScreen from './app/screens/business/projects/ProjectMilestonesScreen';
import ProjectRisksScreen from './app/screens/business/projects/ProjectRisksScreen';
import ProjectTeamScreen from './app/screens/business/projects/ProjectTeamScreen';
import EditProjectScreen from './app/screens/business/projects/EditProjectScreen';
import NotificationsProjectScreen from './app/screens/business/projects/NotificationsProjectScreen';
import BudgetSettingsProjectScreen from './app/screens/business/projects/BudgetSettingsProjectScreen';
import CreateNewContractScreen from './app/screens/business/contracts/CreateNewContractScreen';
import CompanyEventsScreen from './app/screens/business/other/CompanyEventsScreen';
import ProjectListScreen from './app/screens/business/projects/ProjectListScreen';
// Financial Management
import CreateInvoiceScreen from './app/screens/business/finance/CreateInvoiceScreen';
import EditInvoiceScreen from './app/screens/business/finance/EditInvoiceScreen';
import AddSupplierScreen from './app/screens/business/vendors/AddSupplierScreen';
import EditSupplierScreen from './app/screens/business/vendors/EditSupplierScreen';
import FinancialAnalyticsDashboard from './app/screens/business/finance/FinancialAnalyticsDashboard';
import ExpenseTrackingScreen from './app/screens/business/finance/ExpenseTrackingScreen';
import RevenueTrackingScreen from './app/screens/business/finance/RevenueTrackingScreen';
import AnnualRevenueScreen from './app/screens/business/finance/AnnualRevenueScreen';
import AnnualAuditScreen from './app/screens/business/finance/AnnualAuditScreen';


// Interview Management
import RescheduleInterviewScreen from './app/screens/business/recruitment/RescheduleInterviewScreen';
import ScheduleFinalInterviewScreen from './app/screens/business/recruitment/ScheduleFinalInterviewScreen';
import ContactCandidateScreen from './app/screens/business/recruitment/ContactCandidateScreen';

// Vendor Management
import VendorCategories from './app/screens/business/vendors/VendorCategories';
import AddEditSubcontractor from './app/screens/business/vendors/AddEditSubcontractor';
import SubcontractorDetail from './app/screens/business/vendors/SubcontractorDetail';
import ProjectAssignment from './app/screens/business/projects/ProjectAssignment';
import VendorPerformanceDashboard from './app/screens/business/vendors/VendorPerformanceDashboard';
import EditJobScreen from './app/screens/business/recruitment/EditJobScreen';
import OrdersListScreen from './app/screens/business/vendors/OrdersListScreen';
// Compliance Management
import ComplianceDetailsScreen from './app/screens/business/compliance/ComplianceDetailsScreen';
import ComplianceCheckScreen from './app/screens/business/compliance/ComplianceCheckScreen';
import CertificationsScreen from './app/screens/business/compliance/CertificationsScreen';
import RegulatoryUpdateDetailsScreen from './app/screens/business/compliance/RegulatoryUpdateDetailsScreen';
import ComplianceDeadlineDetailsScreen from './app/screens/business/compliance/ComplianceDeadlineDetailsScreen';
import AddEditDeadlineScreen from './app/screens/business/compliance/AddEditDeadlineScreen';
import AlertPreferencesScreen from './app/screens/business/compliance/AlertPreferencesScreen';
import ComplianceReportGeneratorScreen from './app/screens/business/compliance/ComplianceReportGeneratorScreen';

// HR Management
import PayrollManagementScreen from './app/screens/business/hr/PayrollManagementScreen';
import EmployeeManagementScreen from './app/screens/business/hr/EmployeeManagementScreen';
import HRDashboardScreen from './app/screens/business/hr/HRDashboardScreen';
import HiredScreen from './app/screens/business/recruitment/HiredScreen';
import JobPostAnalytics from './app/screens/business/recruitment/JobPostAnalytics';
import JobManagementScreen from './app/screens/business/recruitment/JobManagementScreen';
import CreateJobScreen from './app/screens/business/recruitment/CreateJobScreen';
import JobTemplates from './app/screens/business/recruitment/JobTemplates';
import PostJobDetailsScreen from './app/screens/business/recruitment/PostJobDetailsScreen';
import ApplicantsPipelineScreen from './app/screens/business/recruitment/ApplicantsPipelineScreen';
import ApplicantDetailsScreen from './app/screens/business/recruitment/ApplicantDetailsScreen';
import ManagerDashboard from './app/screens/business/core/ManagerDashboard';
import ContractorDashboard from './app/screens/business/core/ContractorDashboard';
import EmployeeDashboard from './app/screens/business/hr/EmployeeDashboard';
import CreateCustomTemplateScreen from './app/screens/business/documents/CreateCustomTemplateScreen';

// Sales & CRM
import ClientsScreen from './app/screens/business/crm/ClientsScreen';
import SalesForecastingScreen from './app/screens/business/crm/SalesForecastingScreen';
import OpportunityTrackingScreen from './app/screens/business/crm/OpportunityTrackingScreen';
import DealManagementScreen from './app/screens/business/crm/DealManagementScreen';
import LeadAssignmentScreen from './app/screens/business/crm/LeadAssignmentScreen';
import LeadNurturingScreen from './app/screens/business/crm/LeadNurturingScreen';
import LeadScoringScreen from './app/screens/business/crm/LeadScoringScreen';
import PipelineManagementScreen from './app/screens/business/crm/PipelineManagementScreen';
import MobileSalesScreen from './app/screens/business/crm/MobileSalesScreen';
import IntegrationHubScreen from './app/screens/business/core/IntegrationHubScreen';
import AutomationRulesScreen from './app/screens/business/recruitment/AutomationRulesScreen';
import AIInsightsScreen from './app/screens/business/ai/AIInsightsScreen';
import ProposalBuilderScreen from './app/screens/business/contracts/ProposalBuilderScreen';
import QuoteGeneratorScreen from './app/screens/business/contracts/QuoteGeneratorScreen';
import TeamAnalyticsScreen from './app/screens/business/other/TeamAnalyticsScreen';
import RevenueAnalysisScreen from './app/screens/business/finance/RevenueAnalysisScreen';
import SalesDashboardScreen from './app/screens/business/crm/SalesDashboardScreen';
import LeadManagementScreen from './app/screens/business/crm/LeadManagementScreen';
import ProjectManagementScreen from './app/screens/business/projects/ProjectManagementScreen';
import ServiceManagementScreen from './app/screens/business/other/ServiceManagementScreen';
import CustomerRelationsScreen from './app/screens/business/crm/CustomerRelationsScreen';
import ForecastingScreen from './app/screens/business/finance/ForecastingScreen';
import StageManagementScreen from './app/screens/business/recruitment/StageManagementScreen';
import PipelineTrackingScreen from './app/screens/business/crm/PipelineTrackingScreen';
import OpportunityScreen from './app/screens/business/crm/OpportunityScreen';
import SupportTicketsScreen from './app/screens/business/other/SupportTicketsScreen';
import CommunicationHistoryScreen from './app/screens/business/other/CommunicationHistoryScreen';
import CustomerProfilesScreen from './app/screens/business/crm/CustomerProfilesScreen';



// Invoice & Contract Management
import InvoiceSystemScreen from './app/screens/business/finance/InvoiceSystemScreen';
import ContractManagementScreen from './app/screens/business/contracts/ContractManagementScreen';
import ContactManagementScreen from './app/screens/business/crm/ContactManagementScreen';
import CommunicationHubScreen from './app/screens/business/other/CommunicationHubScreen';
import CustomerSupportScreen from './app/screens/business/crm/CustomerSupportScreen';
import FeedbackSystemScreen from './app/screens/business/other/FeedbackSystemScreen';
import ContractsScreen from './app/screens/business/contracts/ContractsScreen';

// Attendance & Leave Management
import AttendanceScreen from './app/screens/business/hr/AttendanceScreen';
import LeaveManagementScreen from './app/screens/business/hr/LeaveManagementScreen';
import VisitTrackingScreen from './app/screens/business/crm/VisitTrackingScreen';
import FollowUpRemindersScreen from './app/screens/business/crm/FollowUpRemindersScreen';

// Email & Communication
import EmailManagementScreen from './app/screens/business/marketing/EmailManagementScreen';
import LettersManagementScreen from './app/screens/business/documents/LettersManagementScreen';
import BiddingScreen from './app/screens/business/other/BiddingScreen';
import TenderScreen from './app/screens/business/vendors/TenderScreen';
import QuotationScreen from './app/screens/business/contracts/QuotationScreen';

// Testing & Assessment
import TestsBankManagementScreen from './app/screens/business/recruitment/TestsBankManagementScreen';
import TestCandidatesPipelineScreen from './app/screens/business/recruitment/TestCandidatesPipelineScreen';
import VideoInterviewPipelineScreen from './app/screens/business/recruitment/VideoInterviewPipelineScreen';
import VideoScreen from './app/screens/business/other/VideoScreen';

// ERP Systems
import EPRScreen from './app/screens/business/erp/EPRScreen';
import EPRDetailsScreen from './app/screens/business/erp/EPRDetailsScreen';
import DocumentsControlScreen from './app/screens/business/documents/DocumentsControlScreen';
import AuditDashboardScreen from './app/screens/business/documents/AuditDashboardScreen';
import ERPSystemScreen from './app/screens/business/erp/ERPSystemScreen';
import ERPModuleScreen from './app/screens/business/erp/ERPModuleScreen';
import ERPModuleDetail from './app/screens/business/erp/ERPModuleDetail';
import ModuleDetailScreen from './app/screens/business/other/ModuleDetailScreen';

import ERPSearchScreen from './app/screens/business/erp/ERPSearchScreen';
import ERPSettingsScreen from './app/screens/business/erp/ERPSettingsScreen';
import ERPReportsScreen from './app/screens/business/erp/ERPReportsScreen';
import ERPCalendarScreen from './app/screens/business/erp/ERPCalendarScreen';
import ERPAnalyticsScreen from './app/screens/business/erp/ERPAnalyticsScreen';
import AIBusinessInsightsScreen from './app/screens/business/erp/AIBusinessInsightsScreen';

// AI & Automation
import AIAgentScreen from './app/screens/business/ai/AIAgentScreen';

// Offer Management
import AcceptedOfferDetailsScreen from './app/screens/business/recruitment/AcceptedOfferDetailsScreen';
import JobOfferDetailsScreen from './app/screens/business/recruitment/JobOfferDetailsScreen';
import EmployeeDetailsScreen from './app/screens/business/hr/EmployeeDetailsScreen';
import CandidateDetailsScreen from './app/screens/business/recruitment/CandidateDetailsScreen';
import FinalInterviewDetailsScreen from './app/screens/business/recruitment/FinalInterviewDetailsScreen';
import CreateOfferScreen from './app/screens/business/contracts/CreateOfferScreen';

// AR & Smart Contracts
import ARDemoScreen from './app/screens/business/other/ARDemoScreen';
import ScheduleFirstDayScreen from './app/screens/business/recruitment/ScheduleFirstDayScreen';
import GenerateSmartContractScreen from './app/screens/business/contracts/GenerateSmartContractScreen';
import NegotiationScreen from './app/screens/business/crm/NegotiationScreen';
import SmartContractTemplates from './app/screens/business/contracts/SmartContractTemplates';

// Project Planning
import ProjectScheduleScreen from './app/screens/business/projects/ProjectScheduleScreen';
import ProjectPlanningScreen from './app/screens/business/projects/ProjectPlanningScreen';

// Website Management
import WebsiteAnalyticsDashboard from './app/screens/business/analytics/WebsiteAnalyticsDashboard';
import WebsiteSyncSystem from './app/screens/business/website/WebsiteSyncSystem';
import ContentManagementScreen from './app/screens/business/website/ContentManagementScreen';
import WebsiteManagementScreen from './app/screens/business/website/WebsiteManagementScreen';
import ProjectProductCreationScreen from './app/screens/business/projects/ProjectProductCreationScreen';
import CompanyAdvertisementsScreen from './app/screens/business/other/CompanyAdvertisementsScreen';

// Admin & Internship Management
import AdminHRManagementScreen from './app/screens/business/hr/AdminHRManagementScreen';
import InternshipAnalyticsDashboard from './app/screens/business/other/InternshipAnalyticsDashboard';
import EvaluationAssessmentScreen from './app/screens/business/recruitment/EvaluationAssessmentScreen';
import MentorDashboardScreen from './app/screens/business/other/MentorDashboardScreen';
import StudentInternshipPortalScreen from './app/screens/professional/StudentInternshipPortalScreen';

// Departments
import CompanySalesDepartmentScreen from './app/screens/business/other/CompanySalesDepartmentScreen';
import CompanyPurchaseDepartmentScreen from './app/screens/business/other/CompanyPurchaseDepartmentScreen';
import LeadCaptureScreen from './app/screens/business/crm/LeadCaptureScreen';
import ActivityScreen from './app/screens/business/core/ActivityScreen';

// Business Integration
import CRMScreen from './app/screens/business/crm/CRMScreen';
import InnovativeScreen from './app/screens/business/other/InnovativeScreen';
import ScaleUpBusinessScreen from './app/screens/business/other/ScaleUpBusinessScreen';
import CompanyAdvancedAnalyticsBIScreen from './app/screens/business/analytics/CompanyAdvancedAnalyticsBIScreen';

// B2B Portal
import ClientDashboardScreen from './app/screens/business/other/ClientDashboardScreen';
import CommunicationsScreen from './app/screens/business/other/CommunicationsScreen';
import InterviewsScreen from './app/screens/business/recruitment/InterviewsScreen';
import ProjectsScreen from './app/screens/business/projects/ProjectsScreen';
import PartnersScreen from './app/screens/business/analytics/PartnersScreen';
import OverviewScreen from './app/screens/business/core/OverviewScreen';

// Business-Professional Integration
import BusinessContactProfessionalScreen from './app/screens/business/core/BusinessContactProfessionalScreen';

// AI Hiring System
import AutomatedHiringPipelineScreen from './app/screens/business/recruitment/AutomatedHiringPipelineScreen';
import AutomationDashboard from './app/screens/business/recruitment/AutomationDashboard';

// Debug & Diagnostic
import DebugJobsScreen from './screens/Debug/DebugJobsScreen';
import JobengAcademyScreen from './screens/JobengAcademyScreen';

// Other
import SalaryComparisonScreen from './screens/SalaryComparisonScreen';
import LogoDesignScreen from './app/screens/business/marketing/LogoDesignScreen';

// employeedashboardCalendarScreen
import TimesheetScreen from './app/screens/business/hr/TimesheetScreen';
import LeaveRequestScreen from './app/screens/business/hr/LeaveRequestScreen';
import TaskListScreen from './app/screens/business/projects/TaskListScreen';
import TaskDetailsScreen from './app/screens/business/projects/TaskDetailsScreen';
import ExpenseReportScreen from './app/screens/business/finance/ExpenseReportScreen';
import TrainingScreen from './app/screens/business/hr/TrainingScreen';
import BenefitsScreen from './app/screens/business/hr/BenefitsScreen';
import PerformanceReviewScreen from './app/screens/business/hr/PerformanceReviewScreen';
import CalendarScreen from './app/screens/business/core/CalendarScreen';
import { ReportsScreen,EmployeeSettingsScreen,EmployeeMessagesScreen,EmployeeEditProfileScreen } from './app/screens/business/hr/EmployeeSubScreens';






// Constants
// Stripe publishable key is fetched at runtime from the backend (GET /api/stripe/config),
// so NO Stripe key is hardcoded in the app. The previous hardcoded value here was a SECRET
// key (sk_...) — a security leak — and has been removed. Revoke that key in the Stripe
// dashboard if you haven't already.
const STRIPE_CONFIG_URL = 'https://api.jobeng.ai/api/stripe/config';
const JOBENG_API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Navigation
const Tab = createBottomTabNavigator();
const Stack = createNativeStackNavigator();

// Initialize dark mode
initializeDarkMode().then(darkMode => {
  console.log('🌓 DarkMode initialized on app start:', darkMode);
}).catch(error => {
  console.warn('⚠️ Could not initialize darkMode:', error);
});

// Ignore logs
LogBox.ignoreLogs([
  'UnifiedIntegrationService',
  'runtime not ready', 
  'Exception in HostFunction',
  'ProfessionalContext',
  'CertificationManagementService',
  'expo-notifications',
  'new NativeEventEmitter',
  'Non-serializable values',
  'VirtualizedLists should never be nested',
  'Require cycle',
  'expo-av',
  'EventEmitter.removeListener',
  'Setting a timer',
  'AsyncStorage has been extracted',
  'is not a valid icon name for family',
  'Require cycles are allowed',
  'ViewPropTypes will be removed',
  'ColorPropType will be removed',
  'exported from \'deprecated-react-native-prop-types\'.',
]);
export const navigateToScreen = (navigation, screenName, params = {}) => {
  if (navigation && navigation.navigate) {
    navigation.navigate(screenName, params);
  } else {
    console.warn(`Navigation to ${screenName} failed: navigation object missing`);
    Alert.alert('Error', 'Unable to navigate. Please try again.');
  }
};
console.log('✅ Global services ready');

// TextInput default props
if (TextInput.defaultProps) {
  TextInput.defaultProps.allowFontScaling = false;
} else {
  TextInput.defaultProps = {};
  TextInput.defaultProps.allowFontScaling = false;
}

// Menu items
const menuItems = [
  {
    title: 'Business Management',
    items: [
      { name: 'Project Planning', icon: 'briefcase', screen: 'ProjectPlanning' },
      { name: 'Tender Management', icon: 'document', screen: 'TenderScreen' },
      { name: 'Bidding System', icon: 'trending-up', screen: 'BiddingScreen' },
      { name: 'Advertisements', icon: 'megaphone', screen: 'CompanyAdvertisements' },
      { name: 'Website Management', icon: 'globe', screen: 'WebsiteManagement' },
    ]
  }
];

// Test auth setup
const setupTestAuth = async () => {
  try {
    // Credentials matching live API
    await AsyncStorage.setItem('auth_token', 'test_token_12345');
    await AsyncStorage.setItem('user_id',    '1');
    await AsyncStorage.setItem('company_id', '1');
    await AsyncStorage.setItem('business_id','1');
    await AsyncStorage.setItem('user_email', 'john.smith@jobeng.in');
    await AsyncStorage.setItem('user_name',  'John Smith');

    if (InterviewIntegrationService && typeof InterviewIntegrationService.initialize === 'function') {
      await InterviewIntegrationService.initialize();
    }
    if (InterviewWorkflowAutomation && typeof InterviewWorkflowAutomation.initialize === 'function') {
      await InterviewWorkflowAutomation.initialize();
    }

    await Crypto.getRandomBytesAsync(16);
    console.log('✅ Auth credentials set — syncing company profile from DB...');

    // Sync company profile + subscription from live DB immediately
    try {
      const { profile, subscription } = await syncAfterLogin({ companyId: '1' });
      if (profile)       console.log('✅ Company synced:', profile.name, '| logo:', !!profile.logo);
      if (subscription)  console.log('✅ Subscription synced:', subscription.plan_name, '£'+subscription.price+'/mo');
    } catch (syncErr) {
      console.warn('⚠ Startup sync failed (non-fatal):', syncErr.message);
    }

  } catch (error) {
    console.error('Error setting up test auth:', error);
  }
};

// Tab Navigator
function TabNavigator() {
  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;
          if (route.name === 'Home') {
            iconName = focused ? 'home' : 'home-outline';
          } else if (route.name === 'Learning') {
            iconName = focused ? 'book' : 'book-outline';
          } else if (route.name === 'Profile') {
            iconName = focused ? 'person' : 'person-outline';
          } else if (route.name === 'More') {
            iconName = focused ? 'menu' : 'menu-outline';
          }
          return <Ionicons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: '#0e4e96',
        tabBarInactiveTintColor: 'gray',
        headerShown: false,
      })}
    >
      <Tab.Screen name="Home" component={HomeScreen} />
      <Tab.Screen name="Learning" component={LearningScreen} />
      <Tab.Screen name="Profile" component={ProfileScreen} />
      <Tab.Screen name="More" component={MoreScreen} />
    </Tab.Navigator>
  );
}

// Business Stack Navigator - For business-specific flows
function BusinessStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="BusinessDashboard" component={BusinessDashboardScreen} />
      <Stack.Screen name="BusinessDashboardScreen" component={BusinessDashboardScreen} />
      <Stack.Screen name="AddEmployeeScreen" component={AddEmployeeScreen} />
      <Stack.Screen name="FinancialManagementScreen" component={FinancialManagementScreen} />
      <Stack.Screen name="JobDetailsScreen" component={JobDetailsScreen} />
      <Stack.Screen name="RecruitmentFlowComponent" component={RecruitmentFlowComponent} />
      <Stack.Screen name="BusinessCompanyInfo" component={BusinessCompanyInfoScreen} />
      <Stack.Screen name="CompanyProfile" component={CompanyProfileScreen} />
      <Stack.Screen name="BusinessUserProfile" component={BusinessUserProfileScreen} />
      <Stack.Screen name="BusinessSubscription" component={BusinessSubscriptionSystem} />
      <Stack.Screen name="TeamManagement" component={TeamManagementScreen} />
      <Stack.Screen name="MultiUserDashboard" component={MultiUserBusinessDashboard} />
      <Stack.Screen name="PostJob" component={PostJobScreen} />
      <Stack.Screen name="ApplicantsPipeline" component={ApplicantsPipelineScreen} />
      <Stack.Screen name="ManageProjects" component={ManageProjectsScreen} />
      <Stack.Screen name="FinancialManagement" component={FinancialManagementScreen} />
      
    </Stack.Navigator>
  );
}

// App Stack Navigator
function AppStack() {
  return (
    <Stack.Navigator initialRouteName="Splash" screenOptions={{ headerShown: false }}>
      {/* Authentication Screens */}
      <Stack.Screen name="Splash" component={SplashScreen} />
      
      {/* Separate Login Screens */}
      <Stack.Screen name="BusinessLogin" component={BusinessLoginScreen} />
      <Stack.Screen name="ProfessionalLogin" component={ProfessionalLoginScreen} />
      <Stack.Screen name="LearningLoginScreen" component={LearningLoginScreen} />
      <Stack.Screen name="UserTypeSelection" component={UserTypeSelectionScreen} />
      <Stack.Screen name="RegisterScreen" component={RegisterScreen} />
      
      {/* Separate Signup Screens */}
      <Stack.Screen name="BusinessSignup" component={BusinessSignUpScreen} />
      <Stack.Screen name="ProfessionalSignup" component={ProfessionalSignupScreen} />
      <Stack.Screen name="LearningSignup" component={LearningSignupScreen} />
      
      {/* Multi-user Business Screens */}
      <Stack.Screen name="MultiUserBusinessSignup" component={MultiUserBusinessSignup} />
      <Stack.Screen name="TeamManagementScreen" component={TeamManagementScreen} />
      <Stack.Screen name="MultiUserBusinessDashboard" component={MultiUserBusinessDashboard} />
      
      {/* Welcome Screens */}
      <Stack.Screen name="WelcomeProfessionalScreen" component={WelcomeProfessionalScreen} />
      <Stack.Screen name="WelcomeBusinessScreen" component={WelcomeBusinessScreen} />
      <Stack.Screen name="WelcomeLearningScreen" component={WelcomeLearningScreen} />
     
      {/* Main Navigation */}
      <Stack.Screen name="Main" component={TabNavigator} />
      <Stack.Screen name="ServerDiagnostic" component={ServerDiagnostic} />
      
      {/* Dashboard Screens */}
      <Stack.Screen name="ProfessionalDashboard" component={ProfessionalDashboardScreen} />
      <Stack.Screen name="BusinessDashboard" component={BusinessDashboardScreen} />
      <Stack.Screen name="LearningDashboard" component={LearningDashboard} />
      
      {/* Business Stack */}
      <Stack.Screen name="BusinessStack" component={BusinessStack} />
      
      {/* Common Screens */}
      <Stack.Screen name="Home" component={HomeScreen} />
      <Stack.Screen name="ChatDetail" component={ChatDetailScreen} />
      <Stack.Screen name="JobengAcademyScreen" component={JobengAcademyScreen} />
      
      {/* Job-related Screens */}
      <Stack.Screen name="FindJobs" component={FindJobsScreen} />
      <Stack.Screen name="BrowseJobs" component={BrowseJobsScreen} />
      <Stack.Screen name="JobDetails" component={JobDetailsScreen} />
      <Stack.Screen name="SubmitForJobs" component={SubmitForJobsScreen} />
      <Stack.Screen name="FreelanceJobs" component={FreelanceJobsScreen} />
      <Stack.Screen name="FullTimeJobsScreen" component={FullTimeJobsScreen} />
      <Stack.Screen name="PartTimeJobsScreen" component={PartTimeJobsScreen} />
      <Stack.Screen name="SearchJobsScreen" component={SearchJobsScreen} />
      <Stack.Screen name="CompanyInfoScreen" component={CompanyInfoScreen} />
      <Stack.Screen name="AIJobMatching" component={AIJobMatching} />
      <Stack.Screen name="ProfessionalHomeScreen" component={ProfessionalHomeScreen} />
      <Stack.Screen name="RemoteJobsScreen" component={RemoteJobsScreen} />
      <Stack.Screen name="JobAlerts" component={JobAlertsScreen} />
      <Stack.Screen name="ApplicationsScreen" component={ApplicationsTrackingScreen} options={{ headerShown: false }} />
      <Stack.Screen name="AnalyticsScreen" component={AnalyticsScreen} />
      <Stack.Screen name="ContractsScreen" component={ContractsScreen} />
      {/* Fixed Business-Professional Integration Screen */}
      <Stack.Screen 
        name="BusinessContactProfessional" 
        component={BusinessContactProfessionalScreen}
        options={{
          headerShown: false,
          presentation: 'modal',
        }}
      />
      <Stack.Screen 
        name="ForgotPassword" 
        component={ForgotPasswordScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CompanySalesDepartmentScreen" 
        component={CompanySalesDepartmentScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CompanyPurchaseDepartmentScreen" 
        component={CompanyPurchaseDepartmentScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="AutomationDashboard" 
        component={AutomationDashboard} 
      />
      <Stack.Screen 
        name="DebugJobsScreen" 
        component={DebugJobsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="LiveVideoCoursesScreen" 
        component={LiveVideoCoursesScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="LiveVideoSession" 
        component={LiveVideoSessionScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="LiveCourseDetails" 
        component={LiveCourseDetailsScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="SessionRecording" 
        component={SessionRecordingScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="MyCourses" 
        component={MyCourses}
        options={{ title: '📚 My Courses' }}
      />
      <Stack.Screen 
        name="BookPreview" 
        component={BookPreview}
        options={{ title: 'Book Preview' }}
      />
      <Stack.Screen 
        name="CourseContent" 
        component={CourseContent}
        options={{ title: 'Course Content' }}
      />
      <Stack.Screen 
        name="CourseAssignments" 
        component={CourseAssignments}
        options={{ title: 'Assignments' }}
      />
      <Stack.Screen 
        name="TodaySchedule" 
        component={TodaySchedule}
        options={{ title: "Today's Schedule" }}
      />
      <Stack.Screen 
        name="Bookmarks" 
        component={Bookmarks}
        options={{ title: 'Bookmarks' }}
      />
      <Stack.Screen 
        name="Downloads" 
        component={Downloads}
        options={{ title: 'Downloads' }}
      />
      <Stack.Screen 
        name="Certificates" 
        component={Certificates}
        options={{ title: 'Certificates' }}
      />
      <Stack.Screen 
        name="CourseCatalog" 
        component={CourseCatalog}
        options={{ title: 'Course Catalog' }}
      />
      <Stack.Screen 
        name="LiveSessions" 
        component={LiveSessions}
        options={{ title: 'Live Sessions' }}
      />
      <Stack.Screen 
        name="ScheduleSession" 
        component={ScheduleSession}
        options={{ title: 'Schedule a Session' }}
      />
      <Stack.Screen 
        name="UKUniversities" 
        component={UKUniversities}
        options={{ title: 'UK Universities' }}
      />
      <Stack.Screen 
        name="UniversityApplications" 
        component={UniversityApplications}
        options={{ title: 'University Application' }}
      />
      <Stack.Screen 
        name="UniversityDetails" 
        component={UniversityDetails}
        options={{ title: 'University Details' }}
      />
      <Stack.Screen 
        name="Scholarships" 
        component={Scholarships}
        options={{ title: 'Scholarships' }}
      />
      <Stack.Screen 
        name="PracticeExams" 
        component={PracticeExams}
        options={{ title: 'Practice Exams' }}
      />
      <Stack.Screen 
        name="VideoLibrary" 
        component={VideoLibrary}
        options={{ title: 'Video Library' }}
      />
      <Stack.Screen 
        name="ResearchPapers" 
        component={ResearchPapers}
        options={{ title: 'Research Papers' }}
      />
      <Stack.Screen 
        name="Internships" 
        component={Internships}
        options={{ title: 'Internships' }}
      />
      <Stack.Screen 
        name="InternshipApply" 
        component={InternshipsApply}
        options={{ title: 'Internships Apply' }}
      />
      <Stack.Screen 
        name="SavedInternships" 
        component={SavedInternships}
        options={{ title: 'Saved Internships' }}
      />
      <Stack.Screen 
        name="JobBoard" 
        component={JobBoard}
        options={{ title: 'Job Board' }}
      />
      <Stack.Screen 
        name="JobApply" 
        component={JobApply}
        options={{ title: 'Job Apply' }}
      />
      <Stack.Screen 
        name="CareerGuidance" 
        component={CareerGuidance}
        options={{ title: 'Career Guidance' }}
      />
      <Stack.Screen 
        name="StudyGroups" 
        component={StudyGroups}
        options={{ title: 'Study Groups' }}
      />
      <Stack.Screen 
        name="Forums" 
        component={Forums}
        options={{ title: 'Forums' }}
      />
      <Stack.Screen 
        name="AskExpert" 
        component={AskExpert}
        options={{ title: 'Ask Expert' }}
      />
      <Stack.Screen 
        name="LearningNotifications" 
        component={LearningNotifications}
        options={{ title: 'Learning Notifications' }}
      />
      <Stack.Screen 
        name="LearningHelpSupport" 
        component={LearningHelpSupport}
        options={{ title: 'Help & Support' }}
      />
      <Stack.Screen 
        name="ProfileSettings" 
        component={ProfileSettings}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ScholarshipFilter" 
        component={ScholarshipFilter}
        options={{ headerShown: false }}
      /> 
      <Stack.Screen 
        name="ScholarshipTips" 
        component={ScholarshipTips}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="FontSizeSettings" 
        component={FontSizeSettings}
        options={{ headerShown: false }}
      /> 
      <Stack.Screen 
        name="LanguageSettings" 
        component={LanguageSettings}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ModuleDetail" 
        component={ModuleDetailScreen}
        options={({ route }) => ({
          title: route.params?.module?.name || 'Module Details',
          headerStyle: { backgroundColor: route.params?.module?.color || '#0e4e96' },
          headerTintColor: 'white',
        })}
      />

      {/* Internship and Career Screens */}
      <Stack.Screen name="InternshipOpportunities" component={InternshipOpportunitiesScreen} />
      <Stack.Screen name="InternshipDetails" component={InternshipDetailsScreen} />
      <Stack.Screen name="InternshipApplication" component={InternshipApplicationScreen} />
      <Stack.Screen name="GraduatePrograms" component={GraduateProgramsScreen} />
      <Stack.Screen name="GraduateProgramApplication" component={GraduateProgramApplicationScreen} />
      <Stack.Screen name="EntryLevelJobs" component={EntryLevelJobsScreen} />
      <Stack.Screen name="JobApplication" component={JobApplicationScreen} options={{ headerShown: false }} />
      <Stack.Screen name="CareerFairs" component={CareerFairsScreen} />
      <Stack.Screen name="CareerFairRegistration" component={CareerFairRegistrationScreen} />
      <Stack.Screen name="MentorshipPrograms" component={MentorshipProgramsScreen} />
      <Stack.Screen name="MentorshipApplication" component={MentorshipApplicationScreen} />
      <Stack.Screen name="SkillDevelopment" component={SkillDevelopmentScreen} />
      <Stack.Screen name="CourseEnrollment" component={CourseEnrollmentScreen} />
      <Stack.Screen name="AllOpportunitiesScreen" component={AllOpportunitiesScreen} />
      <Stack.Screen name="GraduateProgramDetails" component={GraduateProgramDetailsScreen} />
      <Stack.Screen name="CareerFairDetails" component={CareerFairDetailsScreen} />
      <Stack.Screen name="MentorshipProgramDetails" component={MentorshipProgramDetailsScreen} />
      <Stack.Screen name="SCourseDetailsScreen" component={SCourseDetailsScreen} />
      <Stack.Screen name="SkillAssessment" component={SkillAssessmentScreen} />
      <Stack.Screen name="AllEducationScreen" component={AllEducationScreen} />
      <Stack.Screen name="SkillProgress" component={SkillProgressScreen} />
      <Stack.Screen name="CRMScreen" component={CRMScreen} />
      <Stack.Screen name="ActionScreen" component={ActionScreen} options={{ headerShown: false }} />
      <Stack.Screen name="InnovativeScreen" component={InnovativeScreen} />
      <Stack.Screen name="ScaleUpBusinessScreen" component={ScaleUpBusinessScreen} />

      {/* Application Management */}
      <Stack.Screen name="JobApplications" component={JobApplicationsScreen} options={{ headerShown: false }}  />
      <Stack.Screen name="ApplicationDetails" component={ApplicationDetailsScreen} options={{ headerShown: false }} />
      <Stack.Screen name="JobsOpportunities" component={JobsOpportunitiesScreen} />
      <Stack.Screen name="OfferDetails" component={OfferDetails} />
      <Stack.Screen name="JobsOffersScreen" component={JobsOffersScreen} />
      <Stack.Screen name="OfferHistoryScreen" component={OfferHistoryScreen} options={{ headerShown: false }} />
      <Stack.Screen name="CareerCoachingScreen" component={CareerCoachingScreen} />
      <Stack.Screen name="InterviewHistoryScreen" component={InterviewHistoryScreen} />
      <Stack.Screen name="CampaignAIContentAssistantScreen" component={CampaignAIContentAssistantScreen} />
      <Stack.Screen name="LogoDesignScreen" component={LogoDesignScreen} />
      <Stack.Screen name="SalaryComparison" component={SalaryComparisonScreen} />

      {/* Profile Management */}
      <Stack.Screen name="Profile" component={ProfileScreen} />
      <Stack.Screen name="Portfolio" component={PortfolioScreen} />
      <Stack.Screen name="ProfileRating" component={ProfileRatingScreen} />
      <Stack.Screen name="AccountSettings" component={AccountSettingsScreen} />
      <Stack.Screen name="AllActivityScreen" component={AllActivityScreen} />
      <Stack.Screen name="ActivityDetailScreen" component={ActivityDetailScreen} />
      <Stack.Screen name="AddExperience" component={AddExperience} />
      <Stack.Screen name="EditExperience" component={EditExperience} />
      <Stack.Screen name="AddEducation" component={AddEducation} />
      <Stack.Screen name="EditEducation" component={EditEducation} />
      <Stack.Screen name="AddSkill" component={AddSkill} />
      <Stack.Screen name="EditSkill" component={EditSkill} />
      <Stack.Screen name="AddCertification" component={AddCertification} />
      <Stack.Screen name="EditLearningProfileScreen" component={EditLearningProfileScreen} />
      <Stack.Screen name="AllWorkHistoryScreen" component={AllWorkHistoryScreen} />
      <Stack.Screen name="AllProjectsScreen" component={AllProjectsScreen} />
      <Stack.Screen name="AllSkillsScreen" component={AllSkillsScreen} />
      <Stack.Screen name="AllCertificationsScreen" component={AllCertificationsScreen} />
      <Stack.Screen name="EditProfile" component={EditProfileScreen} />
      <Stack.Screen name="AIContentAssistantScreen" component={AIContentAssistantScreen} />
      <Stack.Screen name="NetworkingScreen" component={NetworkingScreen} />
      <Stack.Screen name="MyDocuments" component={MyDocumentsScreen} />
      
      {/* Resume and Cover Letter */}
      <Stack.Screen name="BuildResume" component={BuildResumeScreen} />
      <Stack.Screen name="UploadResume" component={UploadResumeScreen} />
      <Stack.Screen name="ResumePreviewScreen" component={ResumePreviewScreen} />
      <Stack.Screen name="EditEvaluateResume" component={EditEvaluateResumeScreen} />
      <Stack.Screen name="CreateCoverLetter" component={CreateCoverLetterScreen} />
      
      {/* Learning Screens */}
      <Stack.Screen name="Learning" component={LearningScreen} />
      <Stack.Screen name="DevelopmentTraining" component={DevelopmentTrainingScreen} />
      <Stack.Screen name="CourseDetails" component={CourseDetailsScreen} />
      <Stack.Screen name="PCourseDetails" component={PCourseDetails} />
      <Stack.Screen name="CoursePlayerScreen" component={CoursePlayerScreen} />
      <Stack.Screen name="CategoryCoursesScreen" component={CategoryCoursesScreen} />
      <Stack.Screen name="CourseSearch" component={CourseSearchScreen} />
      <Stack.Screen name="WebinarRegistrationScreen" component={WebinarRegistrationScreen} />
      <Stack.Screen name="MyLearningHistoryScreen" component={MyLearningHistoryScreen} />
      <Stack.Screen name="PreviousCoursesScreen" component={PreviousCoursesScreen} />
      <Stack.Screen name="SkillsUpgradeCourses" component={SkillsUpgradeCoursesScreen} />
      <Stack.Screen name="LearningCourses" component={LearningCoursesScreen} />
      <Stack.Screen name="LearningProfileScreen" component={LearningProfileScreen} />
      <Stack.Screen name="EngineeringBooksScreen" component={EngineeringBooksScreen} />
      <Stack.Screen name="VirtualInternshipScreen" component={VirtualInternshipScreen} />
      <Stack.Screen name="BookDetailScreen" component={BookDetailScreen} />
      <Stack.Screen name="BookDetails" component={BookDetailScreen} />
      <Stack.Screen name="FullTOC" component={FullTOC} />
      <Stack.Screen name="Search" component={SearchScreen} />
      <Stack.Screen name="More" component={MoreScreen} />
      <Stack.Screen name="LearningCertificateScreen" component={LearningCertificateScreen} />
      <Stack.Screen name="CourseLearning" component={CourseLearningScreen} />
      <Stack.Screen name="AssignmentScreen" component={AssignmentScreen} />
      <Stack.Screen name="QuizScreen" component={QuizScreen} />
      <Stack.Screen name="AchievementsScreen" component={AchievementsScreen} />
      <Stack.Screen name="CourseLearningPM" component={CourseLearningPMScreen} />  
      <Stack.Screen name="AILearningAssistantScreen" component={AILearningAssistantScreen} />
      <Stack.Screen name="FreeCourses" component={FreeCoursesScreen} />
      <Stack.Screen name="PDFViewer" component={PDFViewer} />

      {/* Tests & Interviews Screens */}
      <Stack.Screen name="TestsInterviews" component={TestsInterviewsScreen} />
      <Stack.Screen name="InterviewDetails" component={InterviewDetailsScreen} />
      <Stack.Screen name="VideoInterview" component={VideoInterviewScreen} />
      <Stack.Screen name="VideoInterviewScreen" component={VideoInterviewScreen} />
      <Stack.Screen name="InterviewPrep" component={InterviewPrep} />
      <Stack.Screen name="RescheduleInterview" component={RescheduleInterview} />
      <Stack.Screen name="AssessmentDetails" component={AssessmentDetails} />
      <Stack.Screen name="AIVideoInterview" component={AIVideoInterviewScreen} />
      <Stack.Screen 
        name="AssessmentResults" 
        component={AssessmentResultsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CompanyManagementScreen" 
        component={CompanyManagementScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ClientsScreen" 
        component={ClientsScreen}
        options={{ headerShown: false }}
      />
      
      {/* Communication */}
      <Stack.Screen name="MessagesScreen" component={MessagesScreen} />
      <Stack.Screen name="NewMessage" component={NewMessage} />
      <Stack.Screen name="Notifications" component={NotificationsScreen} />
      <Stack.Screen name="PrivacySettings" component={PrivacySettingsScreen} />
      
      {/* Services and Support */}
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Events" component={Events} />
      <Stack.Screen name="EventDetail" component={EventDetail} />
      <Stack.Screen name="UKInsights" component={UKInsights} />
      <Stack.Screen name="Support" component={SupportScreen} />
      <Stack.Screen name="NewsTicker" component={NewsTicker} />
      <Stack.Screen name="Settings" component={Settings} />
      <Stack.Screen name="AddNews" component={AddNewsScreen} options={{ title: 'Add News' }} />
      <Stack.Screen name="NewsList" component={NewsListScreen} options={{ title: 'All News' }} />
      <Stack.Screen name="NewsDetail" component={NewsDetailScreen} options={{ title: 'News Detail' }} />
      
      {/* Payment and Subscription */}
      <Stack.Screen name="CoursePurchase" component={CoursePurchaseScreen} />
      <Stack.Screen name="PaymentHistory" component={PaymentHistoryScreen} />
      <Stack.Screen name="SubscriptionStatus" component={SubscriptionStatusScreen} />
      <Stack.Screen name="CancelSubscription" component={CancelSubscriptionScreen} />
      <Stack.Screen name="StripePaymentIntegration" component={StripePaymentIntegration} />
      <Stack.Screen name="Payments" component={PaymentsScreen} />
      <Stack.Screen name="BusinessSubscriptionSystem" component={BusinessSubscriptionSystem} />
      <Stack.Screen name="BusinessPaymentSystem" component={BusinessPaymentSystem} />
      <Stack.Screen name="BusinessUserProfileScreen" component={BusinessUserProfileScreen} />
      <Stack.Screen name="BusinessIntelligenceScreen" component={BusinessIntelligenceScreen} />
      <Stack.Screen name="BusinessCompanyInfoScreen" component={BusinessCompanyInfoScreen} />
      <Stack.Screen name="MakeOfferScreen" component={MakeOfferScreen} />
      <Stack.Screen name="RatingsReviewsScreen" component={RatingsReviewsScreen} />
      <Stack.Screen name="ManageSubscriptionScreen" component={ManageSubscriptionScreen} />

      {/* Business Screens (Individual) */}
      <Stack.Screen name="WelcomeBusiness" component={WelcomeBusinessScreen} />
      <Stack.Screen name="ManageProjects" component={ManageProjectsScreen} />
      <Stack.Screen name="PostJobScreen" component={PostJobScreen} />
      <Stack.Screen name="CompanyProfileScreen" component={CompanyProfileScreen} />
      <Stack.Screen name="FinancialManagement" component={FinancialManagementScreen} />
     <Stack.Screen name="CampaignEmailTemplatesScreen" component={CampaignEmailTemplatesScreen} options={{ headerShown: false }} />
      <Stack.Screen name="ComplianceRegulatory" component={ComplianceRegulatoryScreen} />
      <Stack.Screen name="SuppliersVendorList" component={SuppliersVendorList} />
      <Stack.Screen name="SubcontractorsVendorList" component={SubcontractorsVendorList} />
      <Stack.Screen name="TendersManagement" component={TendersManagement} />
      <Stack.Screen name="InvoicesManagement" component={InvoicesManagement} />
      <Stack.Screen name="BusinessTestsInterviewsScreen" component={BusinessTestsInterviewsScreen} />
      <Stack.Screen name="BusinessVideoConferenceScreen" component={BusinessVideoConferenceScreen} />
      <Stack.Screen name="ComplianceRegulatoryScreen" component={ComplianceRegulatory} />
      <Stack.Screen name="MarketingPromotionScreen" component={MarketingPromotion} />
      <Stack.Screen name="BusinessVideoInterviewScreen" component={BusinessVideoInterviewScreen} />
      <Stack.Screen name="InterviewManagementScreen" component={InterviewManagementScreen} />
      <Stack.Screen name="Applicants" component={Applicants} />
      <Stack.Screen name="EditJob" component={EditJobScreen} />
      <Stack.Screen name="TestApplicantsScreen" component={TestApplicantsScreen} />
      <Stack.Screen name="CandidateManagementScreen" component={CandidateManagementScreen} />
      <Stack.Screen name="AddNewTestScreen" component={AddNewTestScreen} />
      <Stack.Screen name="TestResultsScreen" component={TestResultsScreen} />
      <Stack.Screen name="SmartContractPaymentScreen" component={SmartContractPaymentScreen} />
      <Stack.Screen name="QualifiedApplicantsScreen" component={QualifiedApplicantsScreen} />
      <Stack.Screen name="MarketInsights" component={MarketInsightsScreen} />
      
      <Stack.Screen name="RecruitmentFlow" component={RecruitmentFlowComponent} options={{
            title: 'Recruitment Flow',
            headerShown: false,
          }}
        />
      {/* AI Automated Hiring Pipeline Screen */}
      <Stack.Screen
        name="AutomatedHiringPipeline"
        component={AutomatedHiringPipelineScreen}
        options={{
          title: 'AI Hiring Pipeline',
          headerStyle: {
            backgroundColor: '#FF6B35',
          },
          headerTintColor: '#fff',
        }}
      />
      <Stack.Screen name="CandidateSelectionScreen" component={CandidateSelectionScreen} />
      <Stack.Screen name="VideoInterviewManagementScreen" component={VideoInterviewManagementScreen} />
      <Stack.Screen name="InPersonInterviewScreen" component={InPersonInterviewScreen} />
      <Stack.Screen name="JobOfferManagementScreen" component={JobOfferManagementScreen} />
      <Stack.Screen name="CreateJobOfferScreen" component={CreateJobOfferScreen} />
      <Stack.Screen name="JobOfferLetterTemplate" component={JobOfferLetterTemplate} />
      <Stack.Screen name="JobOfferEmailTemplate" component={JobOfferEmailTemplate} />
      <Stack.Screen name="AcceptedOffersScreen" component={AcceptedOffersScreen} />
      <Stack.Screen name="InternshipManagementScreen" component={InternshipManagementScreen} />
      <Stack.Screen name="AddProjectScreen" component={AddProjectScreen} />
      <Stack.Screen name="ApplicantSelectionScreen" component={ApplicantSelectionScreen} />
      <Stack.Screen name="ScheduleInterviewScreen" component={ScheduleInterviewScreen} />
      <Stack.Screen name="ApplicantsPoolScreen" component={ApplicantsPoolScreen} />
      <Stack.Screen name="ActiveJobsScreen" component={ActiveJobsScreen} />
      <Stack.Screen name="PayrollManagementScreen" component={PayrollManagementScreen} />
      <Stack.Screen name="EmployeeManagementScreen" component={EmployeeManagementScreen} />
      <Stack.Screen name="HRDashboardScreen" component={HRDashboardScreen} />
      <Stack.Screen name="AddEmployeeScreen" component={AddEmployeeScreen} />
      <Stack.Screen name="HiredScreen" component={HiredScreen} />
      <Stack.Screen name="JobPostAnalytics" component={JobPostAnalytics} />
      <Stack.Screen name="JobManagementScreen" component={JobManagementScreen} />
      <Stack.Screen name="CreateJobScreen" component={CreateJobScreen} />
      <Stack.Screen name="JobTemplates" component={JobTemplates} />
      <Stack.Screen name="PostJobDetailsScreen" component={PostJobDetailsScreen} /> 
      <Stack.Screen name="ApplicantsPipelineScreen" component={ApplicantsPipelineScreen} />
      <Stack.Screen name="ApplicantDetailsScreen" component={ApplicantDetailsScreen} />
      <Stack.Screen name="ManagerDashboard" component={ManagerDashboard} />
      <Stack.Screen name="EmployeeDashboard" component={EmployeeDashboard} />
      <Stack.Screen name="ContractorDashboard" component={ContractorDashboard} />
      <Stack.Screen name="CreateCustomTemplateScreen" component={CreateCustomTemplateScreen} />
      <Stack.Screen name="LeadCaptureScreen" component={LeadCaptureScreen} />
      <Stack.Screen name="ActivityScreen" component={ActivityScreen} />
      <Stack.Screen name="SalesForecastingScreen" component={SalesForecastingScreen} />
      <Stack.Screen name="LeadScoringScreen" component={LeadScoringScreen} />
      <Stack.Screen name="LeadNurturingScreen" component={LeadNurturingScreen} />
      <Stack.Screen name="LeadAssignmentScreen" component={LeadAssignmentScreen} />
      <Stack.Screen name="DealManagementScreen" component={DealManagementScreen} />
      <Stack.Screen name="OpportunityTrackingScreen" component={OpportunityTrackingScreen} />
      <Stack.Screen name="PipelineManagementScreen" component={PipelineManagementScreen} />
      <Stack.Screen name="MobileSalesScreen" component={MobileSalesScreen} />
      <Stack.Screen name="IntegrationHubScreen" component={IntegrationHubScreen} />
      <Stack.Screen name="AutomationRulesScreen" component={AutomationRulesScreen} />
      <Stack.Screen name="AIInsightsScreen" component={AIInsightsScreen} />
      <Stack.Screen name="ProposalBuilderScreen" component={ProposalBuilderScreen} />
      <Stack.Screen name="QuoteGeneratorScreen" component={QuoteGeneratorScreen} />
      <Stack.Screen name="TeamAnalyticsScreen" component={TeamAnalyticsScreen} />
      <Stack.Screen name="RevenueAnalysisScreen" component={RevenueAnalysisScreen} />
      <Stack.Screen name="SalesDashboardScreen" component={SalesDashboardScreen} />
      <Stack.Screen name="LeadManagementScreen" component={LeadManagementScreen} />
      <Stack.Screen name="ProjectManagementScreen" component={ProjectManagementScreen} />
      <Stack.Screen name="ServiceManagementScreen" component={ServiceManagementScreen} />
      <Stack.Screen name="CustomerRelationsScreen" component={CustomerRelationsScreen} />
      <Stack.Screen name="ForecastingScreen" component={ForecastingScreen} />
      <Stack.Screen name="StageManagementScreen" component={StageManagementScreen} />
      <Stack.Screen name="PipelineTrackingScreen" component={PipelineTrackingScreen} />
      <Stack.Screen name="OpportunityScreen" component={OpportunityScreen} />
      <Stack.Screen name="SupportTicketsScreen" component={SupportTicketsScreen} />
      <Stack.Screen name="CommunicationHistoryScreen" component={CommunicationHistoryScreen} />
      <Stack.Screen name="CustomerProfilesScreen" component={CustomerProfilesScreen} />

        
      <Stack.Screen name="Timesheet" component={TimesheetScreen} />
      <Stack.Screen name="LeaveRequest" component={LeaveRequestScreen} />
      <Stack.Screen name="TaskList" component={TaskListScreen} />
      <Stack.Screen name="TaskDetails" component={TaskDetailsScreen} />
      <Stack.Screen name="ExpenseReport" component={ExpenseReportScreen} />
      <Stack.Screen name="Training" component={TrainingScreen} />
      <Stack.Screen name="Benefits" component={BenefitsScreen} />
      <Stack.Screen name="PerformanceReview" component={PerformanceReviewScreen} />
      <Stack.Screen name="Calendar" component={CalendarScreen} />
      <Stack.Screen name="Reports" component={ReportsScreen} />
      <Stack.Screen name="EmployeeMessages" component={EmployeeMessagesScreen} />
      <Stack.Screen name="EmployeeSettings" component={EmployeeSettingsScreen} />
      <Stack.Screen name="EmployeeEditProfile" component={EmployeeEditProfileScreen} />
      <Stack.Screen name="ProjectList" component={ProjectListScreen} />
      
      <Stack.Screen name="InvoiceSystemScreen" component={InvoiceSystemScreen} />
      <Stack.Screen name="ContractManagementScreen" component={ContractManagementScreen} />
      <Stack.Screen name="ContactManagementScreen" component={ContactManagementScreen} />
      <Stack.Screen name="CommunicationHubScreen" component={CommunicationHubScreen} />
      <Stack.Screen name="CustomerSupportScreen" component={CustomerSupportScreen} />
      <Stack.Screen name="FeedbackSystemScreen" component={FeedbackSystemScreen} />
      <Stack.Screen name="CompanyAdvancedAnalyticsBIScreen" component={CompanyAdvancedAnalyticsBIScreen} />
      <Stack.Screen name="MachineLearningScreen" component={MachineLearningScreen} />
      <Stack.Screen name="AttendanceScreen" component={AttendanceScreen} />
      <Stack.Screen name="LeaveManagementScreen" component={LeaveManagementScreen} />
      <Stack.Screen name="VisitTrackingScreen" component={VisitTrackingScreen} />
      <Stack.Screen name="FollowUpRemindersScreen" component={FollowUpRemindersScreen} />
      <Stack.Screen name="EmailManagementScreen" component={EmailManagementScreen} />
      <Stack.Screen name="LettersManagementScreen" component={LettersManagementScreen} />
      <Stack.Screen name="BiddingScreen" component={BiddingScreen} />
      <Stack.Screen name="TenderScreen" component={TenderScreen} />
      <Stack.Screen name="QuotationScreen" component={QuotationScreen} />
      <Stack.Screen name="TestCandidatesPipelineScreen" component={TestCandidatesPipelineScreen} />
      <Stack.Screen name="TestsBankManagementScreen" component={TestsBankManagementScreen} />
      <Stack.Screen name="VideoInterviewPipelineScreen" component={VideoInterviewPipelineScreen} />
      <Stack.Screen name="VideoScreen" component={VideoScreen} />
      <Stack.Screen name="EPRScreen" component={EPRScreen} />
      <Stack.Screen name="EPRDetails" component={EPRDetailsScreen} />
      <Stack.Screen name="DocumentsControlScreen" component={DocumentsControlScreen} />
      <Stack.Screen name="AuditDashboard" component={AuditDashboardScreen} />
      <Stack.Screen name="ERPSystemScreen" component={ERPSystemScreen} />
      <Stack.Screen name="ERPModule" component={ERPModuleScreen} />
      <Stack.Screen name="ERPModuleDetail" component={ERPModuleDetail} />
      <Stack.Screen name="AIAgentScreen" component={AIAgentScreen} />

      <Stack.Screen name="ERPSearchScreen" component={ERPSearchScreen} />
      <Stack.Screen name="ERPSettingsScreen" component={ERPSettingsScreen} />
      <Stack.Screen name="ERPReportsScreen" component={ERPReportsScreen} />
      <Stack.Screen name="ERPCalendarScreen" component={ERPCalendarScreen} />
      <Stack.Screen name="ERPAnalyticsScreen" component={ERPAnalyticsScreen} />
      <Stack.Screen name="AIBusinessInsights" component={AIBusinessInsightsScreen} />



      
      <Stack.Screen name="AcceptedOfferDetailsScreen" component={AcceptedOfferDetailsScreen} />
      <Stack.Screen name="JobOfferDetailsScreen" component={JobOfferDetailsScreen} />
      <Stack.Screen name="EmployeeDetailsScreen" component={EmployeeDetailsScreen} />
      <Stack.Screen name="CandidateDetails" component={CandidateDetailsScreen} />
      <Stack.Screen name="FinalInterviewDetailsScreen" component={FinalInterviewDetailsScreen} />
      <Stack.Screen name="CreateOfferScreen" component={CreateOfferScreen} />
      <Stack.Screen name="ARDemoScreen" component={ARDemoScreen} />
      <Stack.Screen name="ScheduleFirstDayScreen" component={ScheduleFirstDayScreen} />
      <Stack.Screen name="GenerateSmartContractScreen" component={GenerateSmartContractScreen} />
      <Stack.Screen name="NegotiationScreen" component={NegotiationScreen} />
      <Stack.Screen name="SmartContractTemplates" component={SmartContractTemplates} />
      <Stack.Screen name="ProjectScheduleScreen" component={ProjectScheduleScreen} />
      <Stack.Screen name="ProjectPlanningScreen" component={ProjectPlanningScreen} />

      <Stack.Screen name="WebsiteAnalyticsDashboard" component={WebsiteAnalyticsDashboard} />
      <Stack.Screen name="WebsiteSyncSystem" component={WebsiteSyncSystem} />
      <Stack.Screen name="ContentManagementScreen" component={ContentManagementScreen} />
      <Stack.Screen name="WebsiteManagementScreen" component={WebsiteManagementScreen} />
      <Stack.Screen name="ProjectProductCreationScreen" component={ProjectProductCreationScreen} />
      <Stack.Screen name="CompanyAdvertisementsScreen" component={CompanyAdvertisementsScreen} />

      <Stack.Screen name="AdminHRManagementScreen" component={AdminHRManagementScreen} />
      <Stack.Screen name="InternshipAnalyticsDashboard" component={InternshipAnalyticsDashboard} />
      <Stack.Screen name="EvaluationAssessmentScreen" component={EvaluationAssessmentScreen} />
      <Stack.Screen name="MentorDashboardScreen" component={MentorDashboardScreen} />
      <Stack.Screen name="StudentInternshipPortalScreen" component={StudentInternshipPortalScreen} />
      <Stack.Screen name="CareerEventScreen" component={CareerEventScreen} />
      
      {/* Marketing and Campaign Management */}
      <Stack.Screen name="BlogEditor" component={BlogEditorScreen} />
      <Stack.Screen name="CampaignDetails" component={CampaignDetailsScreen} />
      <Stack.Screen name="AIContentAssistant" component={AIContentAssistantScreen} />
      <Stack.Screen name="CreateCampaign" component={CreateCampaignScreen} />
      <Stack.Screen name="EmailTemplates" component={EmailTemplatesScreen} />
      <Stack.Screen name="VideoEditor" component={VideoEditorScreen} />
      <Stack.Screen name="ImageCreator" component={ImageCreatorScreen} />
      <Stack.Screen name="CampaignList" component={CampaignListScreen} />
      <Stack.Screen name="CampaignAnalyticsDashboard" component={CampaignAnalyticsDashboard} />
      <Stack.Screen name="AddCampaignAssetScreen" component={AddCampaignAssetScreen} />
      {/* ─── Route aliases: nav calls across the app use these names (audit 12 Jun 2026) ─── */}
      <Stack.Screen name="AddCampaignAsset" component={AddCampaignAssetScreen} />
      <Stack.Screen name="PayrollManagement" component={PayrollManagementScreen} />
      <Stack.Screen name="EmployeeDetails" component={EmployeeDetailsScreen} />
      <Stack.Screen name="ProjectManagement" component={ProjectManagementScreen} />
      <Stack.Screen name="ProjectPlanning" component={ProjectPlanningScreen} />
      <Stack.Screen name="ProjectSchedule" component={ProjectScheduleScreen} />
      <Stack.Screen name="WebsiteManagement" component={WebsiteManagementScreen} />
      <Stack.Screen name="AutomatedHiringPipelineScreen" component={AutomatedHiringPipelineScreen} />
      <Stack.Screen name="UKUniversitiesScreen" component={UKUniversities} />
      <Stack.Screen name="LiveSession" component={LiveSessions} />
      <Stack.Screen name="LiveSessionsScreen" component={LiveSessions} />
      <Stack.Screen name="UniversityApply" component={UniversityApplications} />
      <Stack.Screen name="MyApplications" component={ApplicationsTrackingScreen} />
      <Stack.Screen name="AllForums" component={Forums} />
      <Stack.Screen name="Achievements" component={AchievementsScreen} />
      <Stack.Screen name="AddCertificate" component={AddCertification} />
      <Stack.Screen name="CertificateView" component={Certificates} />
      <Stack.Screen name="LearningCoursesScreen" component={LearningCoursesScreen} />
      <Stack.Screen name="CoursePurchaseScreen" component={CoursePurchaseScreen} />
      {/* ─── End route aliases ─── */}
      <Stack.Screen name="ScheduleSettings" component={ScheduleSettingsScreen} />
      <Stack.Screen name="TargetingSettings" component={TargetingSettingsScreen} />
      <Stack.Screen name="BudgetSettings" component={BudgetSettingsScreen} />
      <Stack.Screen name="EditCampaign" component={EditCampaignScreen} />
      <Stack.Screen name="ExportCampaignScreen" component={ExportCampaignScreen} />
      <Stack.Screen name="NotificationsCampaign" component={NotificationsCampaign} />
      <Stack.Screen name="NotificationSettingsCampaign" component={NotificationSettingsCampaign} />
      <Stack.Screen name="NotificationScheduleCampaign" component={NotificationScheduleCampaign} />
      <Stack.Screen name="NotificationHistoryCampaign" component={NotificationHistoryCampaign} />
      <Stack.Screen name="AnnualAuditScreen" component={AnnualAuditScreen} options={{headerShown:false}} />
      <Stack.Screen name="AnnualRevenueScreen" component={AnnualRevenueScreen} options={{headerShown:false}} />
      
      {/* Project Management */}
      <Stack.Screen name="ProjectAnalytics" component={ProjectAnalyticsScreen} />
      <Stack.Screen name="ProjectDetails" component={ProjectDetailsScreen} />
      <Stack.Screen name="ProjectMilestones" component={ProjectMilestonesScreen} />
      <Stack.Screen name="ProjectRisks" component={ProjectRisksScreen} />
      <Stack.Screen name="ProjectTeam" component={ProjectTeamScreen} />
      <Stack.Screen name="EditProject" component={EditProjectScreen} />
      <Stack.Screen name="NotificationsProjectScreen" component={NotificationsProjectScreen} />
      <Stack.Screen name="BudgetSettingsProjectScreen" component={BudgetSettingsProjectScreen} />
      <Stack.Screen name="CreateNewContractScreen" component={CreateNewContractScreen} />
      <Stack.Screen name="CompanyEventsScreen" component={CompanyEventsScreen} />
      <Stack.Screen name="CompanyAnalytics" component={CompanyAnalytics} />
      
      {/* Financial Management */}
      <Stack.Screen name="CreateInvoiceScreen" component={CreateInvoiceScreen} />
      <Stack.Screen name="EditInvoiceScreen" component={EditInvoiceScreen} />
      <Stack.Screen name="AddSupplierScreen" component={AddSupplierScreen} />
      <Stack.Screen name="EditSupplierScreen" component={EditSupplierScreen} />
      <Stack.Screen name="AddSupplier" component={AddSupplierScreen} options={{ headerShown: false }} />
      <Stack.Screen name="EditSupplier" component={EditSupplierScreen} options={{ headerShown: false }} />
      <Stack.Screen name="FinancialAnalyticsDashboard" component={FinancialAnalyticsDashboard} />
      <Stack.Screen name="ExpenseTrackingScreen" component={ExpenseTrackingScreen} />
      <Stack.Screen name="RevenueTrackingScreen" component={RevenueTrackingScreen} />
      
      {/* Interview Management */}
      <Stack.Screen name="RescheduleInterviewScreen" component={RescheduleInterviewScreen} />
      <Stack.Screen name="ScheduleFinalInterviewScreen" component={ScheduleFinalInterviewScreen} />
      <Stack.Screen name="ContactCandidateScreen" component={ContactCandidateScreen} />
      
      {/* Vendor Management */}
      <Stack.Screen name="VendorCategories" component={VendorCategories} />
      <Stack.Screen name="AddEditSubcontractor" component={AddEditSubcontractor} />
      <Stack.Screen name="SubcontractorDetail" component={SubcontractorDetail} />
      <Stack.Screen name="ProjectAssignment" component={ProjectAssignment} />
      <Stack.Screen name="VendorPerformanceDashboard" component={VendorPerformanceDashboard} />
      
      <Stack.Screen name="OrdersList" component={OrdersListScreen} />

      {/* Main Dashboard - Enhanced B2B Portal */}
      <Stack.Screen name="ClientDashboard" component={ClientDashboardScreen} options={{ gestureEnabled: false }} />
      <Stack.Screen name="CommunicationsScreen" component={CommunicationsScreen} />                
      <Stack.Screen name="InterviewsScreen" component={InterviewsScreen} />         
      <Stack.Screen name="ProjectsScreen" component={ProjectsScreen} /> 
      <Stack.Screen name="PartnersScreen" component={PartnersScreen} /> 
      <Stack.Screen name="OverviewScreen" component={OverviewScreen} />

      {/* Compliance Management */}
      <Stack.Screen name="ComplianceDetailsScreen" component={ComplianceDetailsScreen} options={{ headerShown: true, title: 'Compliance Details', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />
      <Stack.Screen name="ComplianceCheckScreen" component={ComplianceCheckScreen} options={{ headerShown: true, title: 'Compliance Check', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />
      <Stack.Screen name="CertificationsScreen" component={CertificationsScreen} options={{ headerShown: false }} />
      <Stack.Screen name="Certifications" component={CertificationsScreen} options={{ headerShown: false }} />
      {/* Backwards-compat aliases — the two old screens are now merged into CertificationsScreen */}
      <Stack.Screen name="AddEditCertificationScreen" component={CertificationsScreen} options={{ headerShown: false }} />
      <Stack.Screen name="CertificationDetailsScreen" component={CertificationsScreen} options={{ headerShown: false }} />
      <Stack.Screen name="RegulatoryUpdateDetailsScreen" component={RegulatoryUpdateDetailsScreen} options={{ headerShown: true, title: 'Regulatory Update', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />
      <Stack.Screen name="ComplianceDeadlineDetailsScreen" component={ComplianceDeadlineDetailsScreen} options={{ headerShown: true, title: 'Deadline Details', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />
      <Stack.Screen name="AddEditDeadlineScreen" component={AddEditDeadlineScreen} options={({ route }) => ({ headerShown: true, title: route.params?.mode === 'add' ? 'Add Deadline' : 'Edit Deadline', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' })} />
      <Stack.Screen name="AlertPreferencesScreen" component={AlertPreferencesScreen} options={{ headerShown: true, title: 'Alert Preferences', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />
      <Stack.Screen name="ComplianceReportGeneratorScreen" component={ComplianceReportGeneratorScreen} options={{ headerShown: true, title: 'Report Generator', headerStyle: { backgroundColor: '#0e4e96' }, headerTintColor: '#fff' }} />

      {/* ═════════ Navigation-audit patch — fixes 21 unregistered routes ═════════ */}
      {/* Routes that exist as files but were missing from the navigator: */}
      <Stack.Screen name="CourseSearchScreen"      component={CourseSearchScreen} />
      <Stack.Screen name="PaymentScreen"           component={PaymentScreen} />
      <Stack.Screen name="PaymentsScreen"          component={PaymentsScreen} />
      <Stack.Screen name="PaymentScenariosScreen"  component={PaymentScenariosScreen} />
      <Stack.Screen name="JobApplicationsScreen"   component={JobApplicationsScreen} />
      <Stack.Screen name="TestsInterviewsScreen"   component={TestsInterviewsScreen} />

      {/* Aliases — same component, second route name to match call-sites: */}
      <Stack.Screen name="BrowseJobsScreen"        component={BrowseJobsScreen} />
      <Stack.Screen name="ProfessionalLoginScreen" component={ProfessionalLoginScreen} />
      <Stack.Screen name="PortfolioScreen"         component={PortfolioScreen} />
      <Stack.Screen name="CompanyInfo"             component={CompanyInfoScreen} />
      <Stack.Screen name="CourseDetail"            component={PCourseDetails} />
      <Stack.Screen name="CourseDetailsScreen"     component={SCourseDetailsScreen} />
      <Stack.Screen name="InterviewHistory"        component={InterviewHistoryScreen} />
      <Stack.Screen name="Messages"                component={MessagesScreen} />
      <Stack.Screen name="Applications"            component={ApplicationsTrackingScreen} />
      <Stack.Screen name="SearchJobs"              component={SearchJobsScreen} />

      {/* Inferred targets — change the component on the right if the wrong screen opens: */}
      <Stack.Screen name="Login"                   component={ProfessionalLoginScreen} />
      <Stack.Screen name="LoginScreen"             component={ProfessionalLoginScreen} />
      <Stack.Screen name="Jobs"                    component={FindJobsScreen} />
      <Stack.Screen name="Dashboard"               component={ProfessionalDashboardScreen} />
      <Stack.Screen name="WebinarsScreen"          component={WebinarRegistrationScreen} />

      {/* ── Dynamic actionRoute values from notification / alert mocks ── */}
      <Stack.Screen name="ApplicationDetailsScreen" component={ApplicationDetailsScreen} />
      <Stack.Screen name="InterviewPrepScreen"      component={InterviewPrep} />
      <Stack.Screen name="AssessmentResultsScreen"  component={AssessmentResultsScreen} />
      <Stack.Screen name="OfferDetailsScreen"       component={OfferDetails} />
      <Stack.Screen name="JobSearchScreen"          component={FindJobsScreen} />
      <Stack.Screen name="JobSummary"               component={FindJobsScreen} />
      {/* ═════════════════════════════════════════════════════════════════════════ */}
    </Stack.Navigator>
  );
}

// Subscription Stack
function SubscriptionStack() {
  return (
    <Stack.Navigator initialRouteName="CoursePurchase">
      <Stack.Screen name="CoursePurchase" component={CoursePurchaseScreen} />
      <Stack.Screen name="PaymentHistory" component={PaymentHistoryScreen} />
      <Stack.Screen name="SubscriptionStatus" component={SubscriptionStatusScreen} />
      <Stack.Screen name="CancelSubscription" component={CancelSubscriptionScreen} />
      <Stack.Screen name="StripePaymentIntegration" component={StripePaymentIntegration} />
    </Stack.Navigator>
  );
}

// Main App Component
export default function App() {
  // Initialize services on component mount
  useEffect(() => {
    // TEMP: Force refresh company profile logo
    AsyncStorage.getItem('company_profile').then(raw => {
      if (raw) {
        try {
          const p = JSON.parse(raw);
          p.logo = 'https://api.jobeng.ai/uploads/jobeng-logo.png';
          AsyncStorage.setItem('company_profile', JSON.stringify(p));
          console.log('Logo URL updated in cache');
        } catch {}
      }
    }).catch(() => {});
  }, []);
  useEffect(() => {
    console.log('🔄 App mounted, ensuring global services...');
    try {
      if (typeof global !== 'undefined' && !global.apiService) {
        console.log('⚠️ global.apiService missing, reinitializing...');
        safeInitializeGlobalServices();
      }
    } catch (error) {
      console.error('❌ Error checking global services:', error);
    }
    setupTestAuth();
  }, []);

  // Fetch the Stripe publishable key from the backend so no key is hardcoded in the app.
  const [stripeKey, setStripeKey] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(STRIPE_CONFIG_URL, { headers: { 'X-API-Key': JOBENG_API_KEY } });
        const data = await res.json();
        if (!cancelled && data && data.publishableKey) {
          setStripeKey(data.publishableKey);
        } else if (!cancelled) {
          console.warn('Stripe config returned no publishableKey:', data);
        }
      } catch (e) {
        console.error('Failed to load Stripe publishable key:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <ServiceProvider>
  <GestureHandlerRootView>
    <BusinessStateProvider>
      <BusinessErrorBoundary>
        <ProjectProvider>
          <GeneralProvider>
            <DatabaseProvider>
              <AuthProvider>
                <LearningProvider>
                  <ProfessionalProvider>
                    <ProfessionalOnboardingProvider>
                      <BusinessProvider>
                        <StripeProvider publishableKey={stripeKey}>
                          <SafeAreaProvider>
                            <NavigationContainer>
                              <AppStack />
                            </NavigationContainer>
                          </SafeAreaProvider>
                        </StripeProvider>
                      </BusinessProvider>
                    </ProfessionalOnboardingProvider>
                  </ProfessionalProvider>
                </LearningProvider>
              </AuthProvider>
            </DatabaseProvider>
          </GeneralProvider>
        </ProjectProvider>
      </BusinessErrorBoundary>
    </BusinessStateProvider>
  </GestureHandlerRootView>
</ServiceProvider>
  );
}